// When to suggest Augment: too few images, a class with too few examples, or a class the last run scored Needs work.
// Pure, so it can be tested; the Next step tile, the Train page and the Augment page all show what it returns.
import type { AnnotationStats, ClassCount } from "../api/ml";

/** Fewer reviewed original images than this, and no copies yet: copies help. */
export const FEW_IMAGES = 200;
/** A class with fewer reviewed annotations than this is rare... */
export const RARE_ANNOTATIONS = 30;
/** ...and so is one with under a fifth of the largest class's. */
export const RARE_RATIO = 5;

export type AdviceKind = "few" | "rare" | "weak";

export interface AugmentAdvice {
  kind: AdviceKind;
  /** One sentence: what is short, and what to do. */
  text: string;
  /** The class to copy the images of; absent when the advice is about every image. */
  classId?: number;
  className?: string;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Classes with reviewed annotations, smallest first, that are rare by either rule. */
export function rareClasses(counts: ClassCount[]): ClassCount[] {
  const used = counts.filter((c) => c.annotations > 0);
  if (used.length === 0) return [];
  const largest = Math.max(...used.map((c) => c.annotations));
  return used
    .filter((c) => c.annotations < RARE_ANNOTATIONS || c.annotations * RARE_RATIO < largest)
    .sort((a, b) => a.annotations - b.annotations);
}

/**
 * The one augmentation suggestion worth making now, or null. Only once the project can train: before that the answer
 * is to annotate, and copies are made from reviewed images anyway.
 *
 * Order: a class the last run scored Needs work (the strongest evidence), then a rare class, then too few images.
 * `weakClasses` are the class names the last run scored Needs work, weakest first.
 */
export function augmentAdvice(
  s: Pick<AnnotationStats, "can_train" | "reviewed_image_count" | "augmented_reviewed_image_count" | "reviewed_by_class">,
  weakClasses: string[] = [],
): AugmentAdvice | null {
  if (!s.can_train) return null;
  const counts = s.reviewed_by_class ?? [];
  for (const name of weakClasses) {
    const c = counts.find((x) => x.name === name);
    if (c && c.original_images > 0) {
      return {
        kind: "weak", classId: c.class_id, className: c.name,
        text: `${c.name} scored Needs work. More varied examples help: make augmented copies of the ${plural(c.original_images, "image")} with ${c.name}.`,
      };
    }
  }
  const rare = rareClasses(counts).find((c) => c.original_images > 0);
  if (rare) {
    return {
      kind: "rare", classId: rare.class_id, className: rare.name,
      text: `Only ${plural(rare.annotations, `${rare.name} annotation`)}. Make augmented copies of those images so the model sees more of it.`,
    };
  }
  const copies = s.augmented_reviewed_image_count ?? 0;
  const originals = s.reviewed_image_count - copies;
  if (copies === 0 && originals > 0 && originals < FEW_IMAGES) {
    return { kind: "few", text: `Only ${plural(originals, "reviewed image")}. Augmented copies give the model more variety to learn from.` };
  }
  return null;
}
